/**
 * EHR-style vital signs table with collapsible header, abnormal count badge, and flags.
 */
import { FC, useState } from 'react'
import type { VitalSign } from '../types'

export const VitalTable: FC<{ vitals: VitalSign[] }> = ({ vitals }) => {
  const [open, setOpen] = useState(true)
  const abnormalCount = vitals.filter(v => v.flag).length

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-3 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider bg-muted text-muted-foreground hover:bg-muted/80 transition-colors"
      >
        <span>Vital Signs</span>
        <div className="flex items-center gap-1.5">
          {abnormalCount > 0 && (
            <span className="text-[8px] font-bold text-clinical-coral bg-red-500/10 px-1.5 py-0.5 rounded-full">
              {abnormalCount} abnl
            </span>
          )}
          <span className="text-[9px]">{open ? '\u25BC' : '\u25B6'}</span>
        </div>
      </button>
      {open && (
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-card text-muted-foreground/60">
              <th className="text-left px-2.5 py-1 font-normal">Vital</th>
              <th className="text-right px-2 py-1 font-normal">Value</th>
              <th className="text-right px-2 py-1 font-normal">Ref</th>
              <th className="text-center px-1.5 py-1 font-normal w-8"></th>
            </tr>
          </thead>
          <tbody>
            {vitals.map((v, i) => (
              <tr
                key={i}
                className={`border-t border-border ${
                  v.flag === 'high' ? 'bg-red-500/5' : v.flag === 'low' ? 'bg-blue-500/5' : ''
                }`}
              >
                <td className="px-2.5 py-1 text-muted-foreground">{v.icon} {v.label}</td>
                <td className={`px-2 py-1 text-right font-mono font-medium ${
                  v.flag === 'high' ? 'text-clinical-coral' : v.flag === 'low' ? 'text-blue-400' : 'text-foreground'
                }`}>
                  {v.value}
                  <span className="text-[9px] font-normal text-muted-foreground/60 ml-0.5">{v.unit}</span>
                </td>
                <td className="px-2 py-1 text-right text-[9px] text-muted-foreground/60">{v.refRange}</td>
                <td className="px-1.5 py-1 text-center">
                  {v.flag ? (
                    <span className={`text-[8px] font-bold px-1 py-0.5 rounded ${
                      v.flag === 'high' ? 'text-clinical-coral bg-red-500/10' : 'text-blue-400 bg-blue-500/10'
                    }`}>
                      {v.flag === 'high' ? '\u2191' : '\u2193'}
                    </span>
                  ) : (
                    <span className="text-[9px] text-clinical-teal">{'\u2713'}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
